import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

export type AIViewMode = 'hidden' | 'sidebar' | 'floating';
export type AIPreferredMode = 'sidebar' | 'floating';

interface AIViewContextType {
  viewMode: AIViewMode;
  setViewMode: (mode: AIViewMode) => void;
  preferredMode: AIPreferredMode;
  isBubbleCollapsed: boolean;
  setBubbleCollapsed: (collapsed: boolean) => void;
  openSidebar: () => void;
  openFloating: () => void;
  openPreferred: () => void;
  openPreferredWithHistory: () => void;
  close: () => void;
  // Pending message to send when sidebar opens
  pendingMessage: string | null;
  setPendingMessage: (message: string | null) => void;
  // Flag to open directly to history view
  pendingHistoryView: boolean;
  setPendingHistoryView: (value: boolean) => void;
  // Flag to force new conversation (from bubble)
  pendingNewChat: boolean;
  setPendingNewChat: (value: boolean) => void;
}

const AIViewContext = createContext<AIViewContextType | null>(null);

const BUBBLE_COLLAPSED_KEY = 'ai_bubble_collapsed';
const PREFERRED_MODE_KEY = 'ai_preferred_mode';

export const AIViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewMode, setViewMode] = useState<AIViewMode>('hidden');
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingHistoryView, setPendingHistoryView] = useState(false);
  const [pendingNewChat, setPendingNewChat] = useState(false);

  // Initialize bubble collapsed state from localStorage
  const [isBubbleCollapsed, setIsBubbleCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(BUBBLE_COLLAPSED_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Initialize preferred mode from localStorage
  const [preferredMode, setPreferredMode] = useState<AIPreferredMode>(() => {
    try {
      const stored = localStorage.getItem(PREFERRED_MODE_KEY);
      if (stored === 'floating' || stored === 'sidebar') {
        return stored;
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'sidebar'; // Default to sidebar
  });

  // Persist bubble collapsed state to localStorage
  const setBubbleCollapsed = useCallback((collapsed: boolean) => {
    setIsBubbleCollapsed(collapsed);
    try {
      localStorage.setItem(BUBBLE_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Save preferred mode to localStorage
  const savePreferredMode = useCallback((mode: AIPreferredMode) => {
    setPreferredMode(mode);
    try {
      localStorage.setItem(PREFERRED_MODE_KEY, mode);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const openSidebar = useCallback(() => {
    setViewMode('sidebar');
    savePreferredMode('sidebar');
  }, [savePreferredMode]);

  const openFloating = useCallback(() => {
    setViewMode('floating');
    savePreferredMode('floating');
  }, [savePreferredMode]);

  // Open the user's preferred mode (last used)
  const openPreferred = useCallback(() => {
    setViewMode(preferredMode);
  }, [preferredMode]);

  // Open the user's preferred mode and show history view
  const openPreferredWithHistory = useCallback(() => {
    setPendingHistoryView(true);
    setViewMode(preferredMode);
  }, [preferredMode]);

  const close = useCallback(() => {
    setViewMode('hidden');
    setPendingMessage(null);
  }, []);

  // Listen for toggle event from SidebarAIToggle button
  useEffect(() => {
    const handleToggle = () => {
      setViewMode(current => {
        if (current === 'hidden') {
          // Open in preferred mode
          return preferredMode;
        } else {
          // Close
          return 'hidden';
        }
      });
    };

    window.addEventListener('toggle-ai-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-ai-sidebar', handleToggle);
  }, [preferredMode]);

  const value = useMemo<AIViewContextType>(() => ({
    viewMode,
    setViewMode,
    preferredMode,
    isBubbleCollapsed,
    setBubbleCollapsed,
    openSidebar,
    openFloating,
    openPreferred,
    openPreferredWithHistory,
    close,
    pendingMessage,
    setPendingMessage,
    pendingHistoryView,
    setPendingHistoryView,
    pendingNewChat,
    setPendingNewChat,
  }), [viewMode, preferredMode, isBubbleCollapsed, setBubbleCollapsed, openSidebar, openFloating, openPreferred, openPreferredWithHistory, close, pendingMessage, pendingHistoryView, pendingNewChat]);

  return (
    <AIViewContext.Provider value={value}>
      {children}
    </AIViewContext.Provider>
  );
};

export const useAIView = (): AIViewContextType => {
  const context = useContext(AIViewContext);
  if (!context) {
    throw new Error('useAIView must be used within an AIViewProvider');
  }
  return context;
};

export default AIViewContext;
